import { useMemo, useState } from 'react';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import { DmControlRoute } from '../../../patient-files/actions/dm/control/DmControlRoute';
import { ScanComplianceStrip } from '../../../patient-files/overview/ScanComplianceStrip';
import { TaskSidebarPanel } from '../../../work-queue/tasks/TaskSidebarPanel';
import {
    buildSidebarTabs,
    DM_NOTIFICATION_TABS,
    type SidebarTabKey,
    type TabContext
} from '../../../work-queue/tasks/taskSidebarTabs';
import { useInitialSidebarTab } from '../../shared/caseActionFlow';
import { PatientViewHeader } from '../../shared/PatientViewHeader';
import { NotificationLevelCounts } from '../NotificationLevelCounts';
import {
    PatientViewError,
    PatientViewNotFound,
    PatientViewSkeleton
} from '../../shared/PatientViewStates';
import { OpenNotificationsPanel } from './OpenNotificationsPanel';
import { useNotificationListSummary } from './useNotificationListSummary';
import { useNotificationPatientView } from './useNotificationPatientView';
import { useNotificationViewNavigation } from './useNotificationViewNavigation';
import { stepTo } from '../../shared/patientViewState';

const DEFAULT_TAB: SidebarTabKey = 'monitoring';
const VAS_TAB: SidebarTabKey = 'vas';
const NOTIFICATION_TAB_CONTEXT = {
    manageMonitoring: true,
    addVirtualAppointments: true
} satisfies TabContext;

// Work a patient's DM notifications on the left, with their tools on the right, like Manage VAs.
export function NotificationPatientView({ patientId }: { patientId: string }) {
    const nav = useNotificationViewNavigation();
    const view = useNotificationPatientView(patientId, nav.listParams);
    const listSummary = useNotificationListSummary(nav.listParams, view.listTotal);
    const initialTab = useInitialSidebarTab(DEFAULT_TAB);
    const [activeTab, setActiveTab] = useState<string>(initialTab);
    const tabs = useMemo(
        () => buildSidebarTabs(patientId, DM_NOTIFICATION_TABS, null, NOTIFICATION_TAB_CONTEXT),
        [patientId]
    );
    const dmId = usePatientCard(patientId).data?.monitoring?.dmId ?? null;

    if (view.status === 'loading') return <PatientViewSkeleton />;
    if (view.status === 'notFound') {
        return <PatientViewNotFound onBackToList={nav.backToList} />;
    }
    if (view.status === 'error' || !view.patient) {
        return (
            <PatientViewError
                subject="this patient's notifications"
                onRetry={view.retry}
                onBackToList={nav.backToList}
            />
        );
    }
    const goPrevious = stepTo(view.previousPatientId, nav.goToPatient);
    const goNext = stepTo(view.nextPatientId, nav.goToPatient);

    return (
        <div className="flex h-full min-h-0 flex-col gap-3 px-8 pb-4">
            <PatientViewHeader
                patient={view.patient}
                badges={
                    view.patient.openCount > 0 && (
                        <NotificationLevelCounts counts={view.patient.levelCounts} />
                    )
                }
                backLabel="Back to list"
                onBack={nav.backToList}
                onPrevious={goPrevious}
                onNext={goNext}
                position={view.positionLabel}
                listSummary={listSummary}
                onShowVirtualAppointments={() => setActiveTab(VAS_TAB)}
            />
            <div className="flex min-h-0 flex-1 gap-3">
                <div className="flex min-h-0 min-w-0 flex-[3] flex-col gap-3">
                    <ScanComplianceStrip patientId={patientId} dmId={dmId} />
                    <div className="min-h-0 flex-1">
                        <OpenNotificationsPanel
                            patient={view.patient}
                            recentlyResolved={view.recentlyResolved}
                            onNextPatient={goNext}
                            onBackToList={nav.backToList}
                            status={nav.listParams.status}
                        />
                    </div>
                </div>
                <div className="min-h-0 min-w-0 flex-[7]">
                    <TaskSidebarPanel
                        tabs={tabs}
                        activeKey={activeTab}
                        onActiveKeyChange={setActiveTab}
                        keepMounted
                    />
                </div>
            </div>
            <DmControlRoute patientId={patientId} patientName={view.patient.patientName} />
        </div>
    );
}
